import type { MetaConversation } from 'lib-shared-legacy-types';
import type { ReadConversationApiResponse } from 'lib-shared-types-api';

import type { PhoneNumber } from 'apis/numbers';
import type { AppState } from 'bootstrap.store';

import { twilioTokenRegions } from './types';
import type { TwilioTokenRegion } from './types';

/**
 * Normalises a csid that may arrive as a string or number into a number,
 * returning NaN for invalid input.
 */
export function parseCsid(csid: string | number): number {
  return typeof csid === 'string' ? parseInt(csid, 10) : csid;
}

/**
 * Type guard that checks whether an arbitrary value is a valid
 * TwilioTokenRegion.
 */
export function isTwilioTokenRegion(
  value: unknown,
): value is TwilioTokenRegion {
  return twilioTokenRegions.includes(value as TwilioTokenRegion);
}

/** Returns the owned phone numbers from Redux state. */
function collectOwnedNumbers(state: AppState): PhoneNumber[] {
  return state.telephony.staticTelephoneNumbersArray;
}

/**
 * Returns the PBX region for the phone number that matches the given E.164
 * string, or undefined if no match is found.
 */
export function findPbxRegionForE164(
  numbers: PhoneNumber[],
  e164: string,
): TwilioTokenRegion | undefined {
  return numbers.find(({ e164_format }) => e164_format === e164)?.pbx_region;
}

/**
 * Reads the E.164 number whose Twilio region governs the conference for a given
 * conversation's attributes.
 *
 * For callback conversations the actual outbound leg is dialled from the
 * admin-configured `callback_as_number`, which may live in a different Twilio
 * region than the originally-called `provisioned_number`. The agent's device
 * must register on the callback number's edge to land in the same conference as
 * the customer (see IO-4118).
 *
 * Falls back to `provisioned_number` for regular PSTN conversations and for
 * callback conversations with no override configured.
 */
function readOutboundCallNumberFromAttrs(
  attrs: Record<string, unknown> | undefined,
): string | undefined {
  if (attrs == null) {
    return undefined;
  }

  const callbackAs = attrs.callback_as_number;
  if (typeof callbackAs === 'string' && callbackAs.length > 0) {
    return callbackAs;
  }

  const provisioned = attrs.provisioned_number;
  return typeof provisioned === 'string' ? provisioned : undefined;
}

/**
 * Looks up a MetaConversation by conversation ID, checking both the paginated
 * list and the currently open conversation in Redux.
 */
function findMetaConversationById(
  state: AppState,
  csid: number,
): MetaConversation | undefined {
  const current = state.conversation.metaConversation;

  if (current.conversation?.id === csid) {
    return current;
  }

  const maybeMetaConversation = state.conversations.metaConversations.find(
    (m) => m.conversation.id === csid,
  );

  if (maybeMetaConversation) {
    return maybeMetaConversation;
  }

  return undefined;
}

/**
 * Extracts the outbound-call E.164 number from a MetaConversation's
 * conversation attributes (callback_as_number when present, otherwise
 * provisioned_number).
 */
function readOutboundCallNumberFromMetaConversation(
  metaConversation: MetaConversation | undefined,
): string | undefined {
  return readOutboundCallNumberFromAttrs(
    metaConversation?.conversation?.attributes as
      | Record<string, unknown>
      | undefined,
  );
}

/**
 * Resolves the outbound-call E.164 number for a conversation ID by reading the
 * MetaConversation from Redux. See `readOutboundCallNumberFromAttrs` for the
 * callback-vs-provisioned precedence rules.
 */
export function getOutboundCallNumberForCsid(
  state: AppState,
  csid: string | number,
): string | undefined {
  const id = parseCsid(csid);

  if (Number.isNaN(id)) {
    return undefined;
  }

  return readOutboundCallNumberFromMetaConversation(
    findMetaConversationById(state, id),
  );
}

/**
 * Determines the Twilio token region for an outbound call originating from
 * `fromNumber`. First tries to match `fromNumber` directly against owned
 * numbers; falls back to the agent's currently selected outbound number if no
 * direct match is found.
 */
export function resolveOutboundTokenRegionFromState(
  state: AppState,
  fromNumber: string,
): TwilioTokenRegion | undefined {
  const numbers = collectOwnedNumbers(state);
  const fromRegion = findPbxRegionForE164(numbers, fromNumber);

  if (fromRegion) {
    return fromRegion;
  }

  const selected = state.telephony.selectedFromNumber;

  if (!selected?.e164_format) {
    return undefined;
  }

  return (
    findPbxRegionForE164(numbers, selected.e164_format) ?? selected.pbx_region
  );
}

/**
 * Determines the Twilio token region for an inbound/conversation-driven call by
 * looking up the outbound-call number for the given conversation ID and
 * matching it against owned numbers in Redux.
 */
export function resolveConversationLedTokenRegionFromState(
  state: AppState,
  csid: string | number,
): TwilioTokenRegion | undefined {
  const e164 = getOutboundCallNumberForCsid(state, csid);

  if (!e164) {
    return undefined;
  }
  return resolveTokenRegionForOwnedE164(state, e164);
}

/**
 * Maps an E.164 number to a Twilio token region using owned numbers already in
 * Redux (static + dynamic).
 */
export function resolveTokenRegionForOwnedE164(
  state: AppState,
  e164: string,
): TwilioTokenRegion | undefined {
  return findPbxRegionForE164(collectOwnedNumbers(state), e164);
}

/**
 * Reads the outbound-call E.164 number from a `GET /v2/conversations/:csid`
 * response, applying the same callback-vs-provisioned precedence used for
 * Redux-resident conversations.
 */
export function readOutboundCallNumberFromGetConversationResponse(
  data: ReadConversationApiResponse['data'],
): string | undefined {
  return readOutboundCallNumberFromAttrs(
    data.history.main_conversation.attributes as
      | Record<string, unknown>
      | undefined,
  );
}
